import { trimSwiftWhitespaceAndNewlines } from "@/domain/text/SwiftCharacterSet";
import type { ParsedLyrics, SyncedLyricLine } from "./types";

const LRC_PATTERN = /\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\]/g;
const SRT_PATTERN =
	/(\d{2}):(\d{2}):(\d{2}),(\d{1,3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{1,3})/;
const VTT_PATTERN =
	/(?:(\d{2}):)?(\d{2}):(\d{2})\.(\d{1,3})\s*-->\s*(?:(\d{2}):)?(\d{2}):(\d{2})\.(\d{1,3})/;
const SWIFT_INT_MIN = -(2n ** 63n);
const SWIFT_INT_MAX = 2n ** 63n - 1n;

export function parseLyricsUtf8(
	data: Uint8Array,
	filename?: string,
): ParsedLyrics | null {
	try {
		const text = new TextDecoder("utf-8", { fatal: true }).decode(data);
		return parseLyricsText(text, filename);
	} catch {
		return null;
	}
}

export function parseLyricsText(
	input: string,
	filename?: string,
): ParsedLyrics | null {
	const text = trimSwiftWhitespaceAndNewlines(input);
	if (text.length === 0) {
		return null;
	}

	const lowerName = filename?.toLowerCase() ?? "";
	if (lowerName.endsWith(".srt")) {
		const synced = parseSrt(text);
		if (synced !== null && synced.length > 0) {
			return { plain: stripSrtVttHeaders(text), synced };
		}
	}

	if (lowerName.endsWith(".vtt")) {
		const synced = parseVtt(text);
		if (synced !== null && synced.length > 0) {
			return { plain: stripSrtVttHeaders(text), synced };
		}
	}

	if (text.includes("-->")) {
		const synced = parseSrt(text) ?? parseVtt(text);
		if (synced !== null && synced.length > 0) {
			return { plain: stripSrtVttHeaders(text), synced };
		}
	}

	if (lowerName.endsWith(".lrc") || text.includes("[")) {
		const synced = parseLrc(text);
		if (synced !== null && synced.length > 0) {
			const plain = stripTimestamps(text);
			return { plain: plain.length === 0 ? null : plain, synced };
		}
	}

	return { plain: text, synced: null };
}

function parseLrc(text: string): ReadonlyArray<SyncedLyricLine> | null {
	const output: Array<SyncedLyricLine & { readonly sourceOrder: number }> = [];
	let sourceOrder = 0;

	for (const rawLine of splitLines(text)) {
		const matches = [...rawLine.matchAll(LRC_PATTERN)];
		if (matches.length === 0) {
			continue;
		}

		const stripped = trimSwiftWhitespaceAndNewlines(
			rawLine.replace(LRC_PATTERN, ""),
		);
		if (stripped.length === 0) {
			continue;
		}

		for (const match of matches) {
			const minutes = Number(match[1] ?? "0");
			const seconds = Number(match[2] ?? "0");
			const fractionText = match[3];
			const fraction =
				fractionText === undefined
					? 0
					: Number(fractionText) / 10 ** fractionText.length;
			output.push({
				timestamp: minutes * 60 + seconds + fraction,
				end: null,
				text: stripped,
				words: null,
				sourceOrder,
			});
			sourceOrder += 1;
		}
	}

	if (output.length === 0) {
		return null;
	}

	return output
		.toSorted(
			(left, right) =>
				left.timestamp - right.timestamp ||
				left.sourceOrder - right.sourceOrder,
		)
		.map(({ timestamp, end, text: lineText, words }) => ({
			timestamp,
			end,
			text: lineText,
			words,
		}));
}

function parseSrt(text: string): ReadonlyArray<SyncedLyricLine> | null {
	return parseTimedBlocks(
		text,
		SRT_PATTERN,
		(match) => {
			const hours = Number(match[1] ?? "0");
			const minutes = Number(match[2] ?? "0");
			const seconds = Number(match[3] ?? "0");
			const milliseconds = Number(match[4] ?? "0");
			return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
		},
		true,
	);
}

function parseVtt(text: string): ReadonlyArray<SyncedLyricLine> | null {
	return parseTimedBlocks(
		text,
		VTT_PATTERN,
		(match) => {
			const hours = Number(match[1] ?? "0");
			const minutes = Number(match[2] ?? "0");
			const seconds = Number(match[3] ?? "0");
			const milliseconds = Number(match[4] ?? "0");
			return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
		},
		false,
	);
}

function parseTimedBlocks(
	text: string,
	pattern: RegExp,
	timestamp: (match: RegExpMatchArray) => number,
	skipLeadingNumericText: boolean,
): ReadonlyArray<SyncedLyricLine> | null {
	const lines = splitLines(text);
	const output: SyncedLyricLine[] = [];
	let index = 0;

	while (index < lines.length) {
		const line = trimSwiftWhitespaceAndNewlines(lines[index] ?? "");
		const match = line.match(pattern);
		if (match === null) {
			index += 1;
			continue;
		}

		const texts: string[] = [];
		let next = index + 1;
		while (next < lines.length) {
			const candidate = trimSwiftWhitespaceAndNewlines(lines[next] ?? "");
			if (candidate.length === 0) {
				break;
			}
			if (
				skipLeadingNumericText &&
				texts.length === 0 &&
				parsesAsSwiftInt(candidate)
			) {
				next += 1;
				continue;
			}
			if (
				!skipLeadingNumericText &&
				(candidate.toUpperCase().startsWith("WEBVTT") ||
					candidate.toUpperCase().startsWith("NOTE"))
			) {
				next += 1;
				continue;
			}
			texts.push(candidate);
			next += 1;
		}

		const joined = trimSwiftWhitespaceAndNewlines(texts.join(" "));
		if (joined.length > 0) {
			output.push({
				timestamp: timestamp(match),
				end: null,
				text: joined,
				words: null,
			});
		}
		index = next;
	}

	return output.length === 0
		? null
		: output.toSorted((left, right) => left.timestamp - right.timestamp);
}

function stripSrtVttHeaders(text: string): string | null {
	const plain = trimSwiftWhitespaceAndNewlines(
		trimSwiftWhitespaceAndNewlines(text)
			.split(/\r\n|[\n\v\f\r\u0085\u2028\u2029]/u)
			.filter((line) => {
				const trimmed = trimSwiftWhitespaceAndNewlines(line);
				return (
					trimmed.length > 0 &&
					!trimmed.toUpperCase().startsWith("WEBVTT") &&
					!trimmed.includes("-->") &&
					!parsesAsSwiftInt(trimmed)
				);
			})
			.join("\n"),
	);
	return plain.length === 0 ? null : plain;
}

function stripTimestamps(text: string): string {
	return text
		.split(/\r\n|[\n\v\f\r\u0085\u2028\u2029]/u)
		.map((line) =>
			trimSwiftWhitespaceAndNewlines(line.replace(LRC_PATTERN, "")),
		)
		.filter((line) => line.length > 0)
		.join("\n");
}

function splitLines(text: string): string[] {
	return text.split(/\r\n|[\n\v\f\r\u0085\u2028\u2029]/u);
}

function parsesAsSwiftInt(value: string): boolean {
	if (!/^[+-]?\d+$/u.test(value)) {
		return false;
	}
	const parsed = BigInt(value);
	return parsed >= SWIFT_INT_MIN && parsed <= SWIFT_INT_MAX;
}
